import { faker } from '@faker-js/faker';
import type { 
  Order, 
  OrderProduct, 
  OrderStatus, 
  ProductCategory,
  SupportTicket,
  SupportTicketCategory,
  SupportTicketPriority,
  KnowledgeBaseArticle,
  ServiceRequest
} from '../types';

const productCategories: ProductCategory[] = [
  'machinery_parts',
  'custom_components',
  'raw_materials',
  'industrial_supplies',
  'maintenance_services'
];

const supportTicketCategories: SupportTicketCategory[] = [
  'technical_issue',
  'billing',
  'account_management',
  'product_inquiry',
  'training',
  'documentation',
  'other'
];

const priorities: SupportTicketPriority[] = ['low', 'medium', 'high', 'critical'];

function generateOrderProduct(): OrderProduct {
  const isCustom = faker.datatype.boolean(0.3);
  const category = faker.helpers.arrayElement(productCategories);
  
  return {
    id: faker.string.uuid(),
    name: `${category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} - ${faker.commerce.productName()}`,
    category,
    quantity: faker.number.int({ min: 1, max: 50 }),
    unitPrice: faker.number.float({ min: 500, max: 10000, multipleOf: 0.01 }),
    leadTime: faker.number.int({ min: 5, max: 30 }),
    isCustom,
    ...(isCustom && {
      specifications: faker.lorem.paragraph(1),
      drawingNumber: `DRW-${faker.number.int({ min: 1000, max: 9999 })}`
    })
  };
}

export function generateOrders(count: number): Order[] {
  const orders: Order[] = [];
  const now = new Date();
  const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

  for (let i = 0; i < count; i++) {
    const orderDate = faker.date.between({ from: oneYearAgo, to: now });
    const requestedDeliveryDate = faker.date.between({
      from: orderDate,
      to: new Date(orderDate.getTime() + 30 * 24 * 60 * 60 * 1000), // 30 days after order
    });

    // Generate confirmed delivery date, usually within 1-2 days of requested
    const confirmedDeliveryDate = faker.date.between({
      from: requestedDeliveryDate,
      to: new Date(requestedDeliveryDate.getTime() + 2 * 24 * 60 * 60 * 1000), // Up to 2 days after requested
    });

    // Generate revised delivery date, usually the same as confirmed or slightly earlier
    const revisedDeliveryDate = faker.helpers.maybe(() => 
      faker.date.between({
        from: new Date(confirmedDeliveryDate.getTime() - 2 * 24 * 60 * 60 * 1000), // Up to 2 days before confirmed
        to: confirmedDeliveryDate,
      }),
      { probability: 0.3 } // Only 30% of orders have a revised date
    );

    // Generate actual delivery date, usually on time or early
    const actualDeliveryDate = faker.helpers.maybe(() => {
      const targetDate = revisedDeliveryDate || confirmedDeliveryDate;
      // 70% chance of being on time or early, 30% chance of being late
      const isLate = Math.random() < 0.3;
      if (isLate) {
        return faker.date.between({
          from: targetDate,
          to: new Date(targetDate.getTime() + 3 * 24 * 60 * 60 * 1000), // Up to 3 days late
        });
      } else {
        return faker.date.between({
          from: new Date(targetDate.getTime() - 2 * 24 * 60 * 60 * 1000), // Up to 2 days early
          to: targetDate,
        });
      }
    }, { probability: 0.8 }); // 80% of orders have an actual delivery date

    // Determine status based on dates
    let status: OrderStatus;
    if (!actualDeliveryDate) {
      if (Math.random() < 0.7) {
        status = faker.helpers.arrayElement(['in_production', 'final_testing', 'ready_for_shipping', 'in_delivery']);
      } else {
        status = faker.helpers.arrayElement(['rfq', 'quoted', 'in_draft', 'pending_confirmation', 'confirmed']);
      }
    } else {
      status = 'completed';
    }

    const order: Order = {
      id: faker.string.uuid(),
      orderNumber: `ORD-${faker.string.numeric(6)}`,
      orderDate,
      status,
      requestedDeliveryDate,
      confirmedDeliveryDate,
      revisedDeliveryDate: revisedDeliveryDate || null,
      actualDeliveryDate: actualDeliveryDate || null,
      products: Array.from(
        { length: faker.number.int({ min: 1, max: 3 }) },
        generateOrderProduct
      ),
      totalAmount: faker.number.float({ min: 1000, max: 10000, multipleOf: 0.01 }),
      productionProgress: status === 'in_production' ? faker.number.int({ min: 0, max: 100 }) : undefined,
      qualityChecksPassed: status === 'final_testing' ? faker.number.int({ min: 0, max: 5 }) : undefined,
      qualityChecksTotal: status === 'final_testing' ? 5 : undefined,
      userId: faker.string.uuid(),
      companyId: faker.string.uuid(),
      currency: 'USD',
      priority: faker.helpers.arrayElement(['standard', 'express', 'urgent'] as const),
      notes: faker.helpers.maybe(() => faker.lorem.sentence(), { probability: 0.3 }) || '',
    };

    orders.push(order);
  }

  return orders;
}

function generateSupportTicket(): SupportTicket {
  const createdAt = faker.date.past({ years: 1 });
  const updatedAt = faker.date.between({ from: createdAt, to: new Date() });
  const resolvedAt = faker.helpers.maybe(() => 
    faker.date.between({ from: updatedAt, to: new Date() }),
    { probability: 0.7 }
  ) || null;

  const responseTime = faker.number.float({ min: 0.5, max: 24, multipleOf: 0.5 });
  const resolutionTime = resolvedAt ? 
    faker.number.float({ min: 1, max: 72, multipleOf: 0.5 }) : null;

  return {
    id: faker.string.uuid(),
    ticketNumber: `TICKET-${faker.string.numeric(6)}`,
    userId: faker.string.uuid(),
    companyId: faker.string.uuid(),
    title: faker.lorem.sentence(),
    description: faker.lorem.paragraphs(2),
    category: faker.helpers.arrayElement(supportTicketCategories),
    status: resolvedAt ? 'resolved' : faker.helpers.arrayElement(['open', 'in_progress']),
    priority: faker.helpers.arrayElement(priorities),
    createdAt,
    updatedAt,
    resolvedAt,
    assignedTo: faker.helpers.maybe(() => faker.person.fullName(), { probability: 0.8 }) || null,
    responseTime,
    resolutionTime
  };
}

function generateKnowledgeBaseArticle(): KnowledgeBaseArticle {
  return {
    id: faker.string.uuid(),
    title: faker.lorem.sentence(),
    category: faker.helpers.arrayElement(supportTicketCategories),
    content: faker.lorem.paragraphs(5),
    views: faker.number.int({ min: 0, max: 1000 }),
    lastUpdated: faker.date.past({ years: 1 }),
    tags: Array.from({ length: faker.number.int({ min: 1, max: 5 }) }, () => 
      faker.lorem.word()
    )
  };
}

function generateServiceRequest(): ServiceRequest {
  const requestedDate = faker.date.past({ years: 1 });
  const scheduledDate = faker.helpers.maybe(() => 
    faker.date.between({ from: requestedDate, to: new Date() }),
    { probability: 0.8 }
  ) || null;
  const completedDate = scheduledDate ? 
    faker.helpers.maybe(() => 
      faker.date.between({ from: scheduledDate, to: new Date() }),
      { probability: 0.6 }
    ) || null : null;

  return {
    id: faker.string.uuid(),
    requestNumber: `SR-${faker.string.numeric(6)}`,
    userId: faker.string.uuid(),
    companyId: faker.string.uuid(),
    type: faker.helpers.arrayElement(['training', 'consultation', 'maintenance', 'other']),
    title: faker.lorem.sentence(),
    status: completedDate ? 'resolved' : 
      scheduledDate ? 'in_progress' : 
      'open',
    requestedDate,
    scheduledDate,
    completedDate,
    description: faker.lorem.paragraph(),
    assignedTo: faker.helpers.maybe(() => faker.person.fullName(), { probability: 0.7 }) || null
  };
}

export function generateSupportTickets(count: number): SupportTicket[] {
  return Array.from({ length: count }, generateSupportTicket);
}

export function generateKnowledgeBaseArticles(count: number): KnowledgeBaseArticle[] {
  return Array.from({ length: count }, generateKnowledgeBaseArticle);
}

export function generateServiceRequests(count: number): ServiceRequest[] {
  return Array.from({ length: count }, generateServiceRequest);
}